
import { useState, useEffect } from 'react';

interface Statistics {
  totalUsers: number;
  totalStudents: number;
  totalTeachers: number;
  totalCourses: number;
  totalEnrollments: number;
  activeUsers: number;
}

export default function Statistics() {
  const [stats, setStats] = useState<Statistics>({
    totalUsers: 0,
    totalStudents: 0,
    totalTeachers: 0,
    totalCourses: 0,
    totalEnrollments: 0,
    activeUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch statistics from API
  const fetchStatistics = async () => {
    setLoading(true);
    setError('');
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      const headers = {
        'apikey': supabaseKey,
        'Content-Type': 'application/json',
      };

      console.log('Fetching statistics from:', supabaseUrl);

      // Fetch users
      const usersResponse = await fetch(`${supabaseUrl}/functions/v1/get-users`, {
        method: 'GET',
        headers,
      });

      // Fetch courses (không cần teacher_id để lấy tất cả khóa học)
      const coursesResponse = await fetch(`${supabaseUrl}/functions/v1/get-courses`, {
        method: 'GET',
        headers,
      });

      // Fetch enrollments (không cần course_id để lấy tất cả đăng ký)
      const enrollmentsResponse = await fetch(`${supabaseUrl}/functions/v1/get-enrollments`, {
        method: 'GET',
        headers,
      });

      console.log('API Response Status:', {
        users: usersResponse.status,
        courses: coursesResponse.status,
        enrollments: enrollmentsResponse.status
      });

      if (!usersResponse.ok) {
        const errorText = await usersResponse.text();
        console.error('Users API Error:', errorText);
        throw new Error(`Lỗi tải danh sách người dùng: ${usersResponse.status}`);
      }

      if (!coursesResponse.ok) {
        const errorText = await coursesResponse.text();
        console.error('Courses API Error:', errorText);
        throw new Error(`Lỗi tải danh sách khóa học: ${coursesResponse.status}`);
      }

      if (!enrollmentsResponse.ok) {
        const errorText = await enrollmentsResponse.text();
        console.error('Enrollments API Error:', errorText);
        throw new Error(`Lỗi tải danh sách đăng ký: ${enrollmentsResponse.status}`);
      }

      const usersData = await usersResponse.json();
      const coursesData = await coursesResponse.json();
      const enrollmentsData = await enrollmentsResponse.json();

      console.log('Fetched Data:', {
        users: usersData,
        courses: coursesData,
        enrollments: enrollmentsData
      });

      // Handle different response formats
      const users = Array.isArray(usersData) ? usersData : [];
      const courses = Array.isArray(coursesData) ? coursesData : [];
      const enrollments = Array.isArray(enrollmentsData) ? enrollmentsData : [];

      console.log('Processed Arrays:', {
        usersCount: users.length,
        coursesCount: courses.length,
        enrollmentsCount: enrollments.length
      });

      // Calculate statistics
      const totalUsers = users.length;
      const totalStudents = users.filter((u: any) => u.type === 'student' || u.user_type === 'student').length;
      const totalTeachers = users.filter((u: any) => u.type === 'teacher' || u.user_type === 'teacher').length;
      const totalCourses = courses.length;
      const totalEnrollments = enrollments.length;
      const activeUsers = users.filter((u: any) => u.status === 'active').length || totalUsers;

      console.log('Calculated Stats:', {
        totalUsers,
        totalStudents,
        totalTeachers,
        totalCourses,
        totalEnrollments,
        activeUsers
      });

      setStats({
        totalUsers,
        totalStudents,
        totalTeachers,
        totalCourses,
        totalEnrollments,
        activeUsers
      });

    } catch (err: any) {
      console.error('Error fetching statistics:', err);
      setError(err.message || 'Không thể tải dữ liệu thống kê. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2 text-gray-500">
          <i className="ri-loader-4-line animate-spin"></i>
          <span>Đang tải thống kê...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <i className="ri-error-warning-line mr-2"></i>
            {error}
          </div>
          <button
            onClick={fetchStatistics}
            className="ml-4 px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors whitespace-nowrap"
          >
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Thống kê tổng quan</h2>
        <button
          onClick={fetchStatistics}
          className="flex items-center px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-all whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-2"></i>
          Làm mới
        </button>
      </div>

      {/* Main Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-line text-blue-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Tổng người dùng</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-graduation-cap-line text-green-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Học sinh</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalStudents}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-star-line text-purple-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Giáo viên</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalTeachers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <i className="ri-book-line text-yellow-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Khóa học</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCourses}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <i className="ri-file-list-line text-indigo-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Đăng ký khóa học</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalEnrollments}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-heart-line text-red-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Người dùng hoạt động</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeUsers}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tỷ lệ người dùng</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Học sinh</span>
              <div className="flex items-center">
                <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                  <div 
                    className="bg-green-600 h-2 rounded-full"
                    style={{ 
                      width: `${stats.totalUsers > 0 ? (stats.totalStudents / stats.totalUsers) * 100 : 0}%` 
                    }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {stats.totalUsers > 0 ? Math.round((stats.totalStudents / stats.totalUsers) * 100) : 0}%
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Giáo viên</span>
              <div className="flex items-center">
                <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                  <div 
                    className="bg-purple-600 h-2 rounded-full"
                    style={{ 
                      width: `${stats.totalUsers > 0 ? (stats.totalTeachers / stats.totalUsers) * 100 : 0}%` 
                    }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {stats.totalUsers > 0 ? Math.round((stats.totalTeachers / stats.totalUsers) * 100) : 0}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Thống kê khóa học</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Trung bình đăng ký/khóa học</span>
              <span className="text-lg font-bold text-gray-900">
                {stats.totalCourses > 0 ? Math.round(stats.totalEnrollments / stats.totalCourses) : 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Trung bình khóa học/giáo viên</span>
              <span className="text-lg font-bold text-gray-900">
                {stats.totalTeachers > 0 ? Math.round(stats.totalCourses / stats.totalTeachers) : 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Trung bình đăng ký/học sinh</span>
              <span className="text-lg font-bold text-gray-900">
                {stats.totalStudents > 0 ? Math.round(stats.totalEnrollments / stats.totalStudents) : 0}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
